import { getPreloadedConfig } from '../../config';
import { getObjectContentValue } from 'utils';
import { getIsLowerEnvironment } from 'utils/env';
import { EVENTS_MAP } from './constants';
import { AllowedAnalyticsValues, Modifier, EVENT_TYPES } from './types';
import { getPageNameAndSiteSection, validateEvent } from './helpers';
import { store } from '../../store';
import { productCurrencySelector } from 'modules/products/selectors';
import { LOCATION_CHANGE } from 'connected-react-router';
import { personAccountDataSelector, userAuthStatusSelector, userDataSelector } from 'modules/user/selectors';
import { UserHomepage } from 'constants/index';
import { analyticsLayer } from './analyticsLayer';

let lastPath: string | undefined = '';

const AICPA_PREFIX = 'aicpa_';

export const handleEvent = async (payload: any, evntType: string, state?: any) => {
  // Analytics is browser-only. The redux store, and this middleware with it, is
  // also created during server-side rendering, where updateLayer would throw on
  // window. Previously this guard lived inside the tag-manager wait.
  if (typeof window === 'undefined') return;

  const getState = state || store?.getState();
  const isAppInitialized = getState?.app?.appInitialized;
  let type: string = evntType;

  if (type !== LOCATION_CHANGE && !isAppInitialized) return;

  if (!(type in EVENTS_MAP)) return;
  const eventType = EVENTS_MAP[type].type;
  const referrer: string | undefined = eventType === EVENT_TYPES.VIEW ? lastPath : undefined;

  const toHandleEvent = validateEvent(type, getState, { ...payload, referrer });
  if (!toHandleEvent) return;

  const modifier: Modifier = EVENTS_MAP[type].accessors.reduce(
    (acc: Modifier, curr) => {
      let value: AllowedAnalyticsValues =
        curr.path !== '' ? getObjectContentValue(payload, curr.path, curr.default || '') : payload;
      if (curr.processor) value = curr.processor(value, getState);

      // remove a specific key:value pair of the event with nullish values
      const hideNulls = EVENTS_MAP[type].hideNulls;

      if (typeof hideNulls === 'boolean' && hideNulls && (value === null || value === '')) return acc;

      if (typeof hideNulls === 'object') {
        const hideNullKey = hideNulls.find(item => {
          return item === curr.name && (value === null || value === '');
        });
        if (hideNullKey) return acc;
      }

      acc[`${AICPA_PREFIX}${curr.name}`] = value;
      return acc;
    },
    referrer ? { aicpa_dom_referrer: referrer } : {}
  );

  updateLayer(modifier, EVENTS_MAP[type].name, eventType);
  if (eventType === EVENT_TYPES.VIEW) {
    lastPath = window.location.href;
  }
  return modifier;
};

const getPageName = () => {
  const { pageName, siteSection } = getPageNameAndSiteSection();
  return {
    aicpa_page_name: pageName,
    aicpa_site_section: siteSection,
  };
};

const getCurrency = () => {
  const state = store?.getState();
  const currency = state ? productCurrencySelector(state)?.label : 'USD';
  return {
    aicpa_currency: currency,
  };
};

const getUserDetails = () => {
  const state = store?.getState();
  if (!state) return;

  const authStatus = userAuthStatusSelector(state);
  const user = userDataSelector(state);
  const personContactId = personAccountDataSelector(state);

  if (authStatus !== UserHomepage.LOGGED_IN) return;
  if (!authStatus || !user || !personContactId) return;

  return {
    aicpa_user_login: authStatus,
    aicpa_user_id: user.AICPAUID,
    aicpa_user_okta_id: user.userId,
    aicpa_user_person_contact_id: personContactId.personContactId,
  };
};

const updateLayer = (modifier: Modifier, event?: string, type?: string) => {
  (window as any).analyticsData = {
    ...getPageName(),
    ...getCurrency(),
    ...getUserDetails(),
    ...getGenericAttributes((window as any).analyticsData),
    ...modifier,
  };
  if (event) fireEvent(event, type);

  return (window as any).analyticsData;
};

// Names and email addresses must never reach the analytics payload: Google
// prohibits personal data in GA4. Enforced here because this is the one place
// user attributes are carried forward from a previous event.
const FORBIDDEN_ATTRIBUTE = /(email|firstname|lastname|first_name|last_name)/i;

// returns the generic analytics event attributes, removing event-specific ones
export const getGenericAttributes = (data: any) => {
  return data
    ? Object.keys(data)
        .filter(attrKey => !FORBIDDEN_ATTRIBUTE.test(attrKey))
        .filter(
          attrKey =>
            attrKey.startsWith(`${AICPA_PREFIX}user`) ||
            attrKey.startsWith(`${AICPA_PREFIX}page`) ||
            attrKey.startsWith(`${AICPA_PREFIX}site`) ||
            attrKey.startsWith(`${AICPA_PREFIX}dom`)
        )
        .reduce((acc, curr) => {
          return {
            ...acc,
            [curr]: data[curr],
          };
        }, {})
    : {};
};

const fireEvent = async (key: string, type?: string): Promise<any> => {
  if (type === EVENT_TYPES.VIEW) {
    analyticsLayer.view({
      ...(window as any).analyticsData,
      analyticsEvent: key,
    });

    const pathName = (window as any).location.pathname;

    if (typeof (window as any).twq === 'function') {
      (window as any).twq('track', 'PageView');
    }

    if (typeof (window as any).gtag === 'function') {
      (window as any).gtag('config', `${getPreloadedConfig()?.googleTagManagerTag?.id}`, {
        page_path: pathName,
      });
    }

    if (typeof (window as any).uetq !== 'undefined') {
      (window as any).uetq?.push('event', 'page_view', { page_path: pathName });
    }

    if (getIsLowerEnvironment) {
      console.groupCollapsed(`Analytics VIEW event ${key} ${(window as any).analyticsData?.aicpa_page_name}`);
      // tslint:disable-next-line
      console.log(`${key}`);
      // tslint:disable-next-line
      console.log((window as any).analyticsData);
      console.groupEnd();
    }
    return;
  }

  analyticsLayer.link({
    ...(window as any).analyticsData,
    analyticsEvent: key,
  });

  if (getIsLowerEnvironment) {
    console.groupCollapsed(`Analytics LINK event ${key}`);
    // tslint:disable-next-line
    console.log(`${key}`);
    // tslint:disable-next-line
    console.log((window as any).analyticsData);
    console.groupEnd();
  }
};